import {
  CSV_EXPORT_NAMING,
  DEFAULT_CONFIDENCE_THRESHOLD,
} from "../config/gridLinesConfig";
import { getCellTextWithConfidenceFilter } from "./confidenceUtils";
import { convertTableToCSV } from "./tableCalculations";
import { endpoints } from "config";
import { createFileEntry, getDownloadUrl } from "fileUtils";
import * as utils from "utils";

/**
 * Applies confidence filtering to table data before conversion
 * @param {Object} tableData - Table data with headers and rows
 * @param {Object} selectedTable - Selected table with word-level data
 * @param {number} confidenceThreshold - Minimum confidence threshold (0-1)
 * @param {boolean} confidenceFilterEnabled - Whether filtering is enabled
 * @returns {Object} Filtered table data
 */
export const applyConfidenceFilterToTableData = (
  tableData,
  selectedTable,
  confidenceThreshold,
  confidenceFilterEnabled
) => {
  if (!confidenceFilterEnabled || !selectedTable || !tableData) {
    return tableData;
  }

  const filteredRows = tableData.rows.map((row, rowIndex) => {
    return row.map((cell, colIndex) => {
      // Use the same filtering logic as the Excel grid
      // rowIndex is 0-based in tableData, but getCellTextWithConfidenceFilter expects 1-based (+ 1 for header)
      const filteredText = getCellTextWithConfidenceFilter(
        selectedTable,
        rowIndex + 1, // Convert to 1-based row index
        colIndex,
        confidenceThreshold,
        confidenceFilterEnabled
      );
      return filteredText;
    });
  });

  return {
    headers: tableData.headers,
    rows: filteredRows,
  };
};

/**
 * Converts table data to CSV format and uploads it to S3
 * @param {Object} tableData - Table data with headers and rows
 * @param {Object} selectedTable - Selected table with word-level data (optional)
 * @param {number} confidenceThreshold - Minimum confidence threshold (optional)
 * @param {boolean} confidenceFilterEnabled - Whether filtering is enabled (optional)
 * @returns {Promise<{fileId: string, downloadUrl: string}>} Upload result
 */
export const convertAndUploadTableAsCSV = async (
  tableData,
  selectedTable = null,
  confidenceThreshold = DEFAULT_CONFIDENCE_THRESHOLD,
  confidenceFilterEnabled = false
) => {
  // Apply confidence filtering if parameters are provided
  const filteredTableData = applyConfidenceFilterToTableData(
    tableData,
    selectedTable,
    confidenceThreshold,
    confidenceFilterEnabled
  );

  // Convert table data to CSV
  const csvContent = convertTableToCSV(filteredTableData);
  if (!csvContent) {
    throw new Error("No table data to process - unable to generate CSV");
  }

  // Create a blob from CSV content
  const csvBlob = new Blob([csvContent], { type: "text/csv" });
  const csvFile = new File([csvBlob], "original.csv", { type: "text/csv" });

  // Upload CSV file to S3 and wait for it to be processed
  const uploadedFileId = await createFileEntry(csvFile, true, ["text/csv"]);

  // Get download URL for the uploaded CSV
  const downloadUrl = await getDownloadUrl(uploadedFileId);

  return {
    fileId: uploadedFileId,
    downloadUrl: downloadUrl,
  };
};

/**
 * Submits CSV data to Claims AI analysis endpoint
 * @param {string} csvUrl - URL of the CSV file to analyze
 * @returns {Promise<Object>} Analysis result from Claims AI
 */
export const submitCSVForAnalysis = async (csvUrl) => {
  const requestUrl = csvUrl.url || csvUrl;

  const response = await utils.authenticatedFetch(
    endpoints.aiAgents("claims-ai-parsing"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url: requestUrl }),
    }
  );

  const analysisResult = await response.json();

  if (!analysisResult.success) {
    throw new Error(`Claims AI analysis failed: ${analysisResult.error}`);
  }

  return analysisResult;
};

/**
 * Stores extraction results as a JSON file in S3
 * @param {Object} extractionResult - The complete extraction result object
 * @param {string} originalFileName - Original PDF filename for reference
 * @returns {Promise<{fileId: string, downloadUrl: string}>} Storage result
 */
export const storeExtractionResults = async (
  extractionResult,
  originalFileName = "extraction"
) => {
  // Create JSON content with timestamp and metadata
  const extractionData = {
    timestamp: new Date().toISOString(),
    originalFileName,
    extractionResult,
    version: "1.0",
    type: "pdf_extraction_results",
  };

  const jsonContent = JSON.stringify(extractionData, null, 2);

  // Create a blob from JSON content (use text/plain MIME type since server doesn't support application/json)
  const jsonBlob = new Blob([jsonContent], { type: "text/plain" });
  const jsonFileName = `${originalFileName}_extraction_results.json`;
  const jsonFile = new File([jsonBlob], jsonFileName, {
    type: "text/plain",
  });

  // Upload JSON file to S3 - let server auto-detect MIME type without validation
  const uploadedFileId = await createFileEntry(jsonFile, true, null);

  // Get download URL for the uploaded JSON
  const downloadUrl = await getDownloadUrl(uploadedFileId);

  return {
    fileId: uploadedFileId,
    downloadUrl: downloadUrl,
  };
};

/**
 * Retrieves stored extraction results from S3 by file ID
 * @param {string} extractionResultsFileId - File ID of stored extraction results
 * @returns {Promise<Object>} The extraction results data
 */
export const retrieveExtractionResults = async (extractionResultsFileId) => {
  const downloadUrl = await getDownloadUrl(extractionResultsFileId);
  const requestUrl = downloadUrl.url || downloadUrl;

  const response = await fetch(requestUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to retrieve extraction results: ${response.status} ${response.statusText}`
    );
  }

  const extractionData = await response.json();
  return extractionData;
};

/**
 * Complete workflow: Convert table to CSV, upload, and submit for analysis
 * @param {Object} tableData - Table data with headers and rows
 * @param {Object} selectedTable - Selected table with word-level data (optional)
 * @param {number} confidenceThreshold - Minimum confidence threshold (optional)
 * @param {boolean} confidenceFilterEnabled - Whether filtering is enabled (optional)
 * @returns {Promise<{csvFileId: string, csvDownloadUrl: string, analysisResult: Object}>}
 */
export const processTableForAnalysis = async (
  tableData,
  selectedTable = null,
  confidenceThreshold = DEFAULT_CONFIDENCE_THRESHOLD,
  confidenceFilterEnabled = false
) => {
  // Step 1: Convert and upload CSV (with confidence filtering)
  const {
    fileId: csvFileId,
    downloadUrl: csvDownloadUrl,
  } = await convertAndUploadTableAsCSV(
    tableData,
    selectedTable,
    confidenceThreshold,
    confidenceFilterEnabled
  );

  // Step 2: Submit for AI analysis
  const analysisResult = await submitCSVForAnalysis(csvDownloadUrl);

  return {
    csvFileId,
    csvDownloadUrl,
    analysisResult,
  };
};

/**
 * Downloads table data as CSV file with confidence filtering
 * @param {Object} tableData - Table data with headers and rows
 * @param {Object} selectedTable - Selected table with word-level data
 * @param {number} confidenceThreshold - Minimum confidence threshold (0-1)
 * @param {boolean} confidenceFilterEnabled - Whether filtering is enabled
 */
export const downloadTableAsCSV = (
  tableData,
  selectedTable,
  confidenceThreshold = DEFAULT_CONFIDENCE_THRESHOLD,
  confidenceFilterEnabled = false
) => {
  if (!tableData) return;

  // Apply confidence filtering if enabled
  const filteredTableData = applyConfidenceFilterToTableData(
    tableData,
    selectedTable,
    confidenceThreshold,
    confidenceFilterEnabled
  );

  // Convert to CSV
  const csvContent = convertTableToCSV(filteredTableData);

  // Create download link
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  // Generate filename using naming convention from config
  const date = new Date().toISOString().slice(0, 10);
  const { prefix, suffix } = CSV_EXPORT_NAMING;

  // Build filename: [prefix]_table_[suffix]_date.csv
  const filenameParts = [prefix, "table", suffix, date].filter(Boolean); // Remove empty strings

  link.download = `${filenameParts.join("_")}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
